import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 sm:px-6 py-16" data-testid="login-page">
            <div className="panel p-8 sm:p-9">
                <span className="eyebrow text-accent">Members</span>
                <h1 className="font-display text-2xl sm:text-3xl font-black uppercase tracking-[-0.025em] mt-2.5">Welcome back</h1>
                <p className="mt-2.5 text-sm text-dim leading-relaxed">
                    Log in to manage your subscription preferences.
                </p>

                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div>
                        <label className="eyebrow text-dim block mb-2">Email</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email"
                            className="field"
                        />
                    </div>
                    <div>
                        <label className="eyebrow text-dim block mb-2">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            className="field"
                        />
                        <Link
                            to="/forgot-password"
                            data-testid="login-forgot"
                            className="mt-2.5 inline-block font-mono text-[11px] uppercase tracking-wider text-accent link-underline"
                        >
                            Forgot password?
                        </Link>
                    </div>
                    {error && (
                        <p
                            className="flex items-start gap-2.5 rounded-xl border border-neg/30 bg-neg/[0.07] p-3 text-sm text-neg"
                            data-testid="login-error"
                        >
                            <AlertCircle size={16} className="shrink-0 mt-0.5" />
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="w-full rounded-full bg-accent text-accent-foreground py-3 text-sm font-semibold hover:brightness-110 hover:shadow-glow transition-[filter,box-shadow] duration-150 disabled:opacity-50 disabled:hover:shadow-none"
                    >
                        {loading ? "Logging in…" : "Log in"}
                    </button>
                </form>

                <p className="mt-7 pt-6 border-t border-line text-sm text-dim">
                    No account? <Link to="/register" className="text-accent link-underline font-medium">Subscribe free</Link>
                </p>
            </div>
        </div>
    );
}
